// FILE: src/pages/profile/ProfilePrivacyPage.jsx
// My profile › Privacy & records (CD5.2 PIPEDA; MTO 11.x records): what we
// keep and why, your consent, your activity history, and how to see or
// correct your record.
import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { apiUrl } from "../../services/api";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import ProfileShell from "../../components/profile/ProfileShell";
import { Section, StatusChip } from "../../components/ui";

const KEPT = [
  ["Who you are", "Your name, contact details and address, and your licence class and number if you have a licence, to confirm you're eligible and to identify you."],
  ["How you signed in", "Every sign-in attempt, code and identity check, as Ontario's rules require."],
  ["Your learning time", "Time spent in each lesson and each day, breaks and the 5-hour daily limit."],
  ["Your results", "Quiz and assessment results."],
  ["Support and survey", "Your support requests, our replies and your end-of-course survey."]
];

export default function ProfilePrivacyPage() {
  const profile = useSelector((s) => s.profile.profile);
  return (
    <ProfileShell title="Privacy & records" description="What we keep about you, why, and how to see or correct it.">
      <Stack spacing={3}>
        <Section id="consent" title="Your consent">
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            {profile?.hasConsent ? (
              <StatusChip label="Consent given" tone="success" icon={<CheckCircleOutlineIcon />} />
            ) : (
              <StatusChip label="No consent recorded" tone="warning" icon={<RadioButtonUncheckedIcon />} />
            )}
          </Stack>
          <Typography>
            You agreed that we can collect and use the information below to run your course and keep the records the
            Ministry of Transportation requires driving schools to keep.
          </Typography>
        </Section>

        <Section id="kept" title="What we keep">
          <Stack component="dl" spacing={2} sx={{ m: 0 }}>
            {KEPT.map(([title, text]) => (
              <div key={title}>
                <Typography component="dt" sx={{ fontWeight: 650 }}>
                  {title}
                </Typography>
                <Typography component="dd" sx={{ m: 0, color: "text.secondary" }}>
                  {text}
                </Typography>
              </div>
            ))}
          </Stack>
          <Typography sx={{ mt: 2 }}>
            Records are kept for as long as Ontario's rules for driving schools require, then deleted. We never sell your
            information or show you advertising.
          </Typography>
        </Section>

        <Section id="your-record" title="See or correct your record">
          <Stack spacing={2}>
            <Typography>
              Download your full course record at any time: your details, sign-ins, learning time per day, lessons,
              results, support requests, acknowledgements and survey. To correct something, ask student service
              support. We reply within 48 hours.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
              <Button
                component="a"
                href={apiUrl("/api/me/record.pdf")}
                download
                variant="contained"
                startIcon={<DownloadOutlinedIcon />}
                data-testid="download-my-record"
              >
                Download my record (PDF)
              </Button>
              <Button component="a" href={apiUrl("/api/me/record.json")} download variant="outlined">
                Data file (JSON)
              </Button>
              <Button component={RouterLink} to="/student/activity" variant="outlined" startIcon={<HistoryOutlinedIcon />}>
                View my activity
              </Button>
              <Button component={RouterLink} to="/student/support/new">
                Ask for a correction
              </Button>
            </Stack>
          </Stack>
        </Section>
      </Stack>
    </ProfileShell>
  );
}
